import { PauseCircle, CheckCircle2, Archive, FolderClosed } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { ProjectStatus } from '@trackmind/core'

/**
 * How a project's status looks, decided once.
 *
 * `ProjectStatus` has four values and the tree view rendered badges for two of
 * them. `paused` got an icon, `active` got a green badge on desktop, and
 * `completed` and `archived` got nothing at all — so a finished project was
 * pixel-identical to a live one on a phone, where the `active` badge is hidden
 * anyway. That is the whole of the "completed has no tag" complaint: not that
 * the badge was wrong, but that the branch didn't exist.
 *
 * The grid view had its own separate answer (a `secondary` badge and a
 * strikethrough), which is why the two views disagreed about the same project.
 * Both now read this.
 *
 * `active` is deliberately the one status with no icon: it's the norm, and an
 * icon on every row costs 20px of a 390px name field to say "normal".
 */
export interface ProjectStatusMeta {
  label: string
  /** Absent for `active` — see above. */
  Icon: LucideIcon | null
  /** Tailwind classes for the badge. */
  badgeClass: string
  /** Icon colour when shown without a badge (the dense mobile row). */
  iconClass: string
  /** Finished or shelved work reads back at lower contrast. */
  dim: boolean
  /** Only `completed` — struck through, matching the grid view. */
  strike: boolean
}

const META: Record<ProjectStatus, ProjectStatusMeta> = {
  active: {
    label: 'active',
    Icon: null,
    badgeClass: 'text-green-600 border-green-500',
    iconClass: 'text-amber-500',
    dim: false,
    strike: false,
  },
  paused: {
    label: 'paused',
    Icon: PauseCircle,
    badgeClass: 'text-gray-500 border-gray-400',
    iconClass: 'text-gray-500',
    dim: true,
    strike: false,
  },
  completed: {
    label: 'completed',
    Icon: CheckCircle2,
    badgeClass: 'text-green-600 border-green-500',
    iconClass: 'text-green-500',
    dim: true,
    strike: true,
  },
  archived: {
    label: 'archived',
    Icon: Archive,
    badgeClass: 'text-muted-foreground border-muted-foreground/50',
    iconClass: 'text-muted-foreground',
    dim: true,
    strike: false,
  },
}

/** Falls back to `active` for data written before a status existed. */
export function projectStatusMeta(status: ProjectStatus | string | undefined): ProjectStatusMeta {
  return META[(status ?? 'active') as ProjectStatus] ?? META.active
}

/** The folder icon for a project row, coloured by status. */
export function projectIcon(status: ProjectStatus | string | undefined): LucideIcon {
  return projectStatusMeta(status).Icon ?? FolderClosed
}
